import React from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { Box, Button, Divider, Popover, Typography } from "@mui/material";
import { TimePicker as MuiTimePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import Interval from "../store/Interval";
import Rule from "../store/Rule";

export type IntervalEdit = {
  anchorEl: HTMLElement;
  interval: Interval;
  rule: Rule;
  date: Date;
  isOverride: boolean;
};

type Props = {
  editing: IntervalEdit | null;
  onClose: () => void;
  onBeforeMutate: () => void;
  // Turn the block's date into a standalone exception so edits apply only there.
  onMakeException: (date: Date) => void;
};

// Popover for fine-tuning a single availability block: exact start/end times,
// delete, and (for recurring blocks) converting the day into a date exception —
// the Google-Calendar "this day only" edit. Reuses the MobX interval setters.
const IntervalPopover: React.FC<Props> = observer(
  ({ editing, onClose, onBeforeMutate, onMakeException }) => {
    const { classes } = useStyles();

    return (
      <Popover
        open={!!editing}
        anchorEl={editing?.anchorEl ?? null}
        onClose={onClose}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
      >
        {editing && (
          <Box className={classes.root}>
            <Typography className={classes.title}>
              {editing.isOverride
                ? `Availability · ${format(editing.date, "MMM d")}`
                : `Every ${format(editing.date, "EEEE")}`}
            </Typography>
            <Box className={classes.times}>
              <MuiTimePicker
                label="Start"
                value={editing.interval.startTime ?? null}
                onAccept={(date: Date | null) => {
                  onBeforeMutate();
                  editing.interval.setIntervalStartTime(date);
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!editing.interval.errorMessage,
                  },
                }}
              />
              <Typography className={classes.dash}>–</Typography>
              <MuiTimePicker
                label="End"
                value={editing.interval.endTime ?? null}
                onAccept={(date: Date | null) => {
                  onBeforeMutate();
                  editing.interval.setIntervalEndTime(date);
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!editing.interval.errorMessage,
                  },
                }}
              />
            </Box>
            {editing.interval.errorMessage && (
              <Typography className={classes.error}>
                {editing.interval.errorMessage}
              </Typography>
            )}

            {!editing.isOverride && (
              <>
                <Divider className={classes.divider} />
                <Typography className={classes.hint}>
                  These hours repeat every {format(editing.date, "EEEE")}.
                </Typography>
                <Button
                  size="small"
                  startIcon={<EventRepeatIcon />}
                  onClick={() => onMakeException(editing.date)}
                >
                  Edit this date only
                </Button>
              </>
            )}

            <Box className={classes.actions}>
              <Button
                color="error"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  onBeforeMutate();
                  editing.rule.removeInterval(editing.interval);
                  onClose();
                }}
              >
                Delete
              </Button>
              <Button variant="contained" size="small" onClick={onClose}>
                Done
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    );
  }
);

const useStyles = makeStyles()(() => ({
  root: {
    padding: 16,
    minWidth: 300,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    color: "#363E45",
    marginBottom: 12,
  },
  times: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  dash: {
    color: "#7D8797",
  },
  error: {
    marginTop: 6,
    fontSize: 12,
    color: "#963832",
  },
  divider: {
    margin: "12px 0",
  },
  hint: {
    fontSize: 12,
    color: "#7D8797",
    marginBottom: 4,
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
  },
}));

export default IntervalPopover;
